import type { CreateItemRequest, Item, ItemKind, Trip } from '@itin/shared'
import {
  appendToNotes,
  formatZoneTime,
  instantToWallClock,
  shiftInstantByDays,
  wallClockToInstant,
  zoneCityLabel,
} from '@itin/shared'
import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { useLocale, useT } from '../lib/i18n.js'
import { SnippetPicker } from './SnippetPicker.js'
import { TimeZonePicker } from './TimeZonePicker.js'
import {
  Button,
  ErrorNotice,
  Field,
  GuidanceNote,
  Input,
  Select,
  TextArea,
  sheetBar,
  sheetBody,
  sheetHead,
  sheetShell,
  useSheetScrollLock,
} from './ui.js'

/**
 * Adding or editing one thing on a day.
 *
 * The order is the order Ana described building an itinerary in: **when, what,
 * then what to say about it.** Time first because that is what she knows first —
 * the flight lands at 07:05, the table is booked for 20:00 — and because a day
 * that sorts itself as she types is a day she does not have to reorder.
 *
 * Everything else is behind "more details". Address, phone, site and
 * confirmation number are real fields that matter on the traveller's page, and
 * they are also four inputs she does not fill for most entries. Showing all
 * eight at once made the common case — "09:00, Musée Rodin" — look like work.
 *
 * There is no latitude or longitude. She never types coordinates; the pin comes
 * from the address on save. See server/src/lib/geocode.ts.
 */

export const ITEM_KINDS: readonly ItemKind[] = [
  'flight',
  'train',
  'transfer',
  'car',
  'hotel',
  'restaurant',
  'cafe',
  'bar',
  'museum',
  'activity',
  'shop',
  'note',
  'other',
]

/**
 * A glyph per kind.
 *
 * Text, not an icon font and not an SVG set — it costs nothing, it renders in
 * the printed copy, and it survives a browser that never loads the stylesheet.
 */
export const KIND_GLYPH: Record<ItemKind, string> = {
  flight: '✈',
  train: '🚆',
  transfer: '🚗',
  car: '🚙',
  hotel: '🛏',
  restaurant: '🍴',
  cafe: '☕',
  bar: '🍷',
  museum: '🏛',
  activity: '🎟',
  shop: '🛍',
  note: '📝',
  other: '📍',
}

/** Which kinds usually want a booking reference, so details opens for them. */
const USUALLY_BOOKED = new Set<ItemKind>(['flight', 'train', 'hotel', 'transfer', 'car'])

export function ItemForm({
  trip,
  dayDate,
  existing,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  trip: Trip
  /** The day being edited, so a time can be typed without repeating the date. */
  dayDate: string | null
  existing?: Item
  busy: boolean
  error: unknown
  onSubmit(input: CreateItemRequest): void
  onCancel(): void
}) {
  const t = useT()
  const { locale } = useLocale()

  const [kind, setKind] = useState<ItemKind>(existing?.kind ?? 'other')
  const [elsewhere, setElsewhere] = useState(Boolean(existing?.timezone))
  const [zone, setZone] = useState(existing?.timezone ?? trip.timezone)
  /**
   * Whether this entry ENDS somewhere else, and where.
   *
   * Opened automatically for an item that already has one, so editing a flight
   * shows the arrival zone rather than hiding it behind a toggle Ana has to
   * remember to look for.
   */
  const [lands, setLands] = useState(Boolean(existing?.endTimezone))
  const [endZone, setEndZone] = useState(
    existing?.endTimezone ?? existing?.timezone ?? trip.timezone,
  )
  const [showDetails, setShowDetails] = useState(
    Boolean(existing?.address || existing?.confirmationRef || existing?.phone || existing?.website),
  )

  /**
   * The note, and the library of prose that can be poured into it.
   *
   * Controlled rather than left to `defaultValue` like the other text fields,
   * because inserting a text has to leave the box holding what it held plus the
   * new paragraph — which is not something an uncontrolled input can be told.
   *
   * There is no server route for this side of the feature, deliberately. The
   * item being written may not exist yet, so there is nothing to append TO; the
   * copy is made here, through the same `appendToNotes` the day route uses, and
   * the form's ordinary save commits it. Copy-not-reference holds either way —
   * what lands in the column is text, and nothing records where it came from.
   */
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [pickingSnippet, setPickingSnippet] = useState(false)
  const snippets = useQuery({
    queryKey: ['snippets'],
    queryFn: api.listSnippets,
    enabled: pickingSnippet,
  })

  const effectiveZone = elsewhere ? zone : trip.timezone

  // A time on its own, because the date is the day this form sits on. Asking
  // for a full datetime here means retyping the date she already chose.
  const [time, setTime] = useState(() =>
    existing?.startsAt ? instantToWallClock(new Date(existing.startsAt), effectiveZone).slice(11) : '',
  )

  /**
   * When it ends, read on the ARRIVAL clock.
   *
   * There was no end-time field at all: `ends_at` existed in the schema and was
   * rendered on the traveller's page, and nothing could set it. So a flight had
   * a departure and no landing.
   */
  const effectiveEndZone = lands ? endZone : effectiveZone
  const [endTime, setEndTime] = useState(() =>
    existing?.endsAt ? instantToWallClock(new Date(existing.endsAt), effectiveEndZone).slice(11) : '',
  )

  const instant =
    time && dayDate ? wallClockToInstant(`${dayDate}T${time}`, effectiveZone) : null

  /**
   * The arrival instant, rolled to the next day when the clock reads earlier
   * than departure.
   *
   * A red-eye GRU→CDG leaves at 22:10 and lands at 13:30 — on the following
   * day, and on a different clock. Taking the arrival at face value on the
   * departure date produces a flight that lands nine hours before it left, and
   * an itinerary that sorts itself into nonsense.
   */
  const endInstant = (() => {
    if (!endTime || !dayDate) return null
    const sameDay = wallClockToInstant(`${dayDate}T${endTime}`, effectiveEndZone)
    if (!sameDay) return null
    if (!instant || sameDay.getTime() >= instant.getTime()) return sameDay
    return shiftInstantByDays(sameDay, 1, effectiveEndZone)
  })()

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const text = (key: string) => {
      const value = String(form.get(key) ?? '').trim()
      return value ? { [key]: value } : {}
    }

    onSubmit({
      kind,
      title: String(form.get('title')),
      ...(instant ? { startsAt: instant.toISOString() } : {}),
      ...(endInstant ? { endsAt: endInstant.toISOString() } : { endsAt: null }),
      ...(elsewhere && zone !== trip.timezone ? { timezone: zone } : {}),
      // Only when it genuinely lands elsewhere. Writing the departure zone in
      // here for every entry would make `end_timezone ?? timezone` meaningless.
      ...(lands && endZone !== (elsewhere ? zone : trip.timezone)
        ? { endTimezone: endZone }
        : { endTimezone: null }),
      ...text('address'),
      ...text('addressLocal'),
      ...text('phone'),
      ...text('website'),
      ...text('confirmationRef'),
      ...text('notes'),
    })
  }

  // Full-screen on a phone, so the fields scroll under a title bar and the save
  // button stays reachable. See `sheetShell` for why this is classes rather
  // than a wrapper component.
  useSheetScrollLock(true)

  return (
    <form onSubmit={submit} data-testid="item-form" className={sheetShell}>
      {/* Only rendered on a phone, where a full-screen panel with no title and
          no way out reads as the app having navigated somewhere. */}
      <div className={sheetHead}>
        <span className="font-display text-lg">
          {existing ? t('item.editTitle') : t('item.addTitle')}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>

      <div className={sheetBody}>
        {/* When, what, and what to say about it — in that order. */}
        <div className="grid gap-3 sm:grid-cols-[8rem_8rem_1fr]">
          <Field label={t('item.field.time')}>
            <Input
              name="time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
            />
          </Field>
          {/* Blank for almost everything. A flight without one is half a flight. */}
          <Field label={t('item.field.endTime')}>
            <Input
              name="endTime"
              type="time"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </Field>
          <Field label={t('item.field.kind')}>
            <Select
              name="kind"
              value={kind}
              onChange={(event) => {
                const next = event.target.value as ItemKind
                setKind(next)
                if (USUALLY_BOOKED.has(next)) setShowDetails(true)
              }}
            >
              {ITEM_KINDS.map((value) => (
                <option key={value} value={value}>
                  {KIND_GLYPH[value]}  {t(`item.kind.${value}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {/* Their explanations go here, at row width — see `GuidanceNote`. */}
        <GuidanceNote keys={['item.field.time.guidance', 'item.field.kind.guidance']} />

        <Field label={t('item.field.title')} guidance="item.field.title.guidance">
          <Input
            name="title"
            required
            autoFocus
            defaultValue={existing?.title ?? ''}
            maxLength={200}
          />
        </Field>

        <Field label={t('item.field.notes')} guidance="item.field.notes.guidance">
          <TextArea
            name="notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {/* Directly under the box it writes into. */}
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPickingSnippet((v) => !v)}
          >
            {t('snippets.addFrom')}
          </Button>
        </div>

        {pickingSnippet && (
          <SnippetPicker
            snippets={snippets.data ?? []}
            loading={snippets.isPending}
            here={zoneCityLabel(trip.timezone, locale)}
            // Never busy: picking here is a local edit to a textarea, not a
            // request. Nothing reaches the server until the form is saved.
            busy={false}
            onPick={(entry) => {
              setNotes((current) => appendToNotes(current, entry.body))
              setPickingSnippet(false)
            }}
            onCancel={() => setPickingSnippet(false)}
          />
        )}

        {/* Four fields she does not fill most of the time. Open for a flight or a
            hotel, where a confirmation number is the whole point. */}
        <div>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="-mx-2 inline-flex min-h-11 items-center px-2 text-sm font-medium text-slate-600 underline decoration-gold/50 underline-offset-4 hover:text-slate-900 sm:min-h-0"
          >
            {showDetails ? t('item.fewerDetails') : t('item.moreDetails')}
          </button>
        </div>

        {showDetails && (
          <div className="flex flex-col gap-4 border-l-2 border-gold/30 pl-4">
            <Field label={t('item.field.address')} guidance="item.field.address.guidance">
              <Input name="address" defaultValue={existing?.address ?? ''} maxLength={500} />
            </Field>
            <Field
              label={t('item.field.addressLocal')}
              hint={t('places.field.addressLocalHint')}
              guidance="item.field.addressLocal.guidance"
            >
              <Input
                name="addressLocal"
                defaultValue={existing?.addressLocal ?? ''}
                maxLength={500}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('item.field.phone')} guidance="item.field.phone.guidance">
                {/* `inputMode` so a phone opens the keypad rather than the
                    alphabet — the number is the only thing this field holds. */}
                <Input
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  defaultValue={existing?.phone ?? ''}
                  placeholder="+33144186110"
                />
              </Field>
              <Field label={t('item.field.website')}>
                <Input
                  name="website"
                  type="url"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  defaultValue={existing?.website ?? ''}
                />
              </Field>
            </div>
            <Field
              label={t('item.field.confirmationRef')}
              guidance="item.field.confirmationRef.guidance"
            >
              <Input
                name="confirmationRef"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                defaultValue={existing?.confirmationRef ?? ''}
                maxLength={200}
              />
            </Field>
          </div>
        )}

        {/* Only worth asking once there is a time to be wrong about. */}
        {time && (
          <div className="flex flex-col gap-2 rounded-md bg-white p-3">
            <Field label={t('item.field.timezone')} guidance="item.field.timezone.guidance">
              <Select
                value={elsewhere ? 'elsewhere' : 'trip'}
                onChange={(event) => setElsewhere(event.target.value === 'elsewhere')}
              >
                <option value="trip">
                  {t('timezone.sameAsTrip', { city: zoneCityLabel(trip.timezone, locale) })}
                </option>
                <option value="elsewhere">{t('timezone.elsewhere')}</option>
              </Select>
            </Field>

            {elsewhere && <TimeZonePicker value={zone} onChange={setZone} />}

            {instant && effectiveZone !== trip.timezone && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {t('timezone.readsAs', {
                  time: formatZoneTime(effectiveZone, locale, instant),
                  city: zoneCityLabel(effectiveZone, locale),
                  tripTime: formatZoneTime(trip.timezone, locale, instant),
                  tripCity: zoneCityLabel(trip.timezone, locale),
                })}
              </p>
            )}

            {/*
              Only worth asking once there is an end time to land. This is the
              question the product got wrong: two instants, one zone, so an
              arrival was read on the departure clock.
            */}
            {endTime && (
              <div className="flex flex-col gap-2 border-t border-slate-200 pt-3">
                <Field label={t('item.field.endTimezone')} guidance="item.field.endTimezone.guidance">
                  <Select
                    value={lands ? 'elsewhere' : 'same'}
                    onChange={(event) => setLands(event.target.value === 'elsewhere')}
                  >
                    <option value="same">
                      {t('timezone.landsSame', {
                        city: zoneCityLabel(effectiveZone, locale),
                      })}
                    </option>
                    <option value="elsewhere">{t('timezone.landsElsewhere')}</option>
                  </Select>
                </Field>

                {lands && <TimeZonePicker value={endZone} onChange={setEndZone} />}

                {/*
                  The whole point, stated back to her: departure and arrival on
                  their own clocks, and the fact that it lands the next day if
                  it does. A red-eye is the case somebody gets wrong by hand.
                */}
                {instant && endInstant && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {t('timezone.flightReadsAs', {
                      depTime: formatZoneTime(effectiveZone, locale, instant),
                      depCity: zoneCityLabel(effectiveZone, locale),
                      arrTime: formatZoneTime(effectiveEndZone, locale, endInstant),
                      arrCity: zoneCityLabel(effectiveEndZone, locale),
                    })}
                    {endInstant.getTime() - instant.getTime() > 0 &&
                      ` ${t('timezone.duration', {
                        hours: String(
                          Math.round((endInstant.getTime() - instant.getTime()) / 3_600_000),
                        ),
                      })}`}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {error ? <ErrorNotice error={error} /> : null}
      </div>

      <div className={sheetBar}>
        {/* Full width on a phone: the primary action of a full-screen panel
            should not be a small box floating in a wide bar. */}
        <Button type="submit" variant="primary" disabled={busy} className="max-sm:flex-1">
          {existing ? t('common.save') : t('item.add')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="max-sm:hidden">
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
